import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Strings from '../../../../Strings';
import { Input } from '../../../../containers/Form';
import { actions, ALERTS_GET_RESULT, ALERT_DEFINITION_GET_RESULT, GOALS_GET_RESULT } from './actions';
import {
  convertKilogramToPound,
  roundToDecimal,
  convertCelsiusToFahrenheit,
} from '../../../../utils/unitConverters';
import { notificationActions } from '../../../../components/Notification/redux/actions';
import { actions as alertActions, DELETE_ALERT_RESULT } from '../../../Alerts/redux/actions';
import { actions as patientActions } from '../redux/actions';
import { Button, HEADER_BUTTON_DARK_BLUE } from '../../../../components/PageHeader/Button';
import { PERMISSIONS, hasPermission } from '../../../../utils/userPermissions';
import { EDIT_SCHEDULE } from '../../Schedules/redux/constants';
import { EditRpmScheduleModalPages } from '../../../../modals/_ModalsMetadata/ModalsMetadata';
import { openModalAction } from '../../../../actions/modal';
import { getVitalsForReadingsEnum, ReadingsEnum } from '../../../../utils';
import { VITALS } from '../../../../utils/readings';
import { RPM_PATIENT_STATUS } from '../../../../constants';
import { defaultGoals, EDIT_VITALS_LIMITS_AND_GOALS_MODAL_NAME } from '../../../../modals/EditVitalsLimitsAndGoalsModal';

export const ReadingsEnumToAlertVitalMap = {
  [ReadingsEnum.weight]: 'weight',
  [ReadingsEnum.bloodPressure]: 'blood_pressure',
  [ReadingsEnum.glucose]: 'glucose',
  [ReadingsEnum.temperature]: 'temperature',
  [ReadingsEnum.spirometer]: 'spirometer',
  [ReadingsEnum.pulseox]: 'saturation',
  [ReadingsEnum.heartRate]: 'heart_rate',
  [ReadingsEnum.pm25]: 'air_quality',
  [ReadingsEnum.coagulation]: 'coagulation',
};

export const ReadingsEnumToGoalVitalMap = {
  [ReadingsEnum.weight]: 'weight',
  [ReadingsEnum.bloodPressure]: 'blood_pressure',
  [ReadingsEnum.glucose]: 'glucometer',
  [ReadingsEnum.temperature]: 'temperature',
  [ReadingsEnum.pulseox]: 'pulseox',
  [ReadingsEnum.heartRate]: 'heart_rate',
};

export const ReadingsEnumToGoalFieldsMap = {
  [ReadingsEnum.weight]: ['weight'],
  [ReadingsEnum.bloodPressure]: ['pressure_systolic', 'pressure_diastolic'],
  [ReadingsEnum.glucose]: ['sugar_level'],
  [ReadingsEnum.temperature]: ['temperature'],
  [ReadingsEnum.pulseox]: ['oxygen_saturation'],
  [ReadingsEnum.heartRate]: ['heart_rate'],
};

function LimitsSection(props) {
  const [highWeight, setHighWeight] = useState(null);
  const [lowWeight, setLowWeight] = useState(null);
  const [lowTemperature, setLowTemperature] = useState(null);
  const [highTemperature, setHighTemperature] = useState(null);
  const [highSystolic, setHighSystolic] = useState(null);
  const [lowSystolic, setLowSystolic] = useState(null);
  const [highDiastolic, setHighDiastolic] = useState(null);
  const [lowDiastolic, setLowDiastolic] = useState(null);
  const [lowGlucoseConcentration, setLowGlucoseConcentration] = useState(null);
  const [highGlucoseConcentration, setHighGlucoseConcentration] = useState(null);
  const [lowFev1, setLowFev1] = useState(null);
  const [highFev1, setHighFev1] = useState(null);
  const [lowFvc, setLowFvc] = useState(null);
  const [highFvc, setHighFvc] = useState(null);
  const [lowSaturation, setLowSaturation] = useState(null);
  const [lowHR, setLowHR] = useState(null);
  const [highHR, setHighHR] = useState(null);
  const [highPm25, setHighPm25] = useState(null);
  const [lowPt, setLowPt] = useState(null);
  const [highPt, setHighPt] = useState(null);
  const [lowInr, setLowInr] = useState(null);
  const [highInr, setHighInr] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [alertId, setAlertId] = useState();
  const [readingSchedule, setReadingSchedule] = useState();
  const [goals, setGoals] = useState(defaultGoals);
  const { onAlertCleared, selectedChart, patientId } = props;
  const { getAlertDefinition, getAlerts, deleteAlert, getReadingsSchedules, editReadingsSchedule, getGoals } = props;

  const canEdit = hasPermission(
    PERMISSIONS.CARE_MANAGEMENT_PATIENT_MEDICATION_EDIT || PERMISSIONS.CARE_MANAGEMENT_PATIENT_DEVICES_EDIT,
  );
  const showAlertThresholds = hasPermission(PERMISSIONS.CARE_MANAGEMENT_PATIENT_ALERT_TRESHOLDS);
  const showGoals = hasPermission(PERMISSIONS.CARE_MANAGEMENT_PATIENT_VITAL_GOALS);
  const { patientStatus, enrollmentId } = props;

  const getLimits = async () => {
    setIsLoading(true);
    setReadingSchedule(null);
    const readingsSchedulesResp = await getReadingsSchedules(patientId);
    const readingsSchedules = readingsSchedulesResp.response?.schedules;

    let resp;
    switch (selectedChart) {
      case ReadingsEnum.weight: {
        const schedule = readingsSchedules?.find(s => s?.vitals?.includes(VITALS.WEIGHT) && s.enrollment_id === enrollmentId);
        if (schedule) {
          setReadingSchedule(schedule);
        }
        resp = await getAlertDefinition(patientId, 'weight');
        if (resp.type === ALERT_DEFINITION_GET_RESULT) {
          const max = resp.response?.conditions?.[0]?.max;
          const min = resp.response?.conditions?.[0]?.min;
          max && setHighWeight(roundToDecimal(convertKilogramToPound(max), 2));
          min && setLowWeight(roundToDecimal(convertKilogramToPound(min), 2));
        }

        setIsLoading(false);
        break;
      }
      case ReadingsEnum.bloodPressure: {
        const schedule = readingsSchedules?.find(s => s?.vitals?.includes(VITALS.PRESSURE_SYSTOLIC) && s.enrollment_id === enrollmentId);
        if (schedule) {
          setReadingSchedule(schedule);
        }
        getAlertDefinition(patientId, 'blood_pressure').then(resp => {
          if (resp.type === ALERT_DEFINITION_GET_RESULT) {
            resp.response.conditions?.forEach(condition => {
              if (condition.field === VITALS.PRESSURE_SYSTOLIC) {
                condition.max && setHighSystolic(condition.max);
                condition.min && setLowSystolic(condition.min);
              } else if (condition.field === VITALS.PRESSURE_DIASTOLIC) {
                condition.max && setHighDiastolic(condition.max);
                condition.min && setLowDiastolic(condition.min);
              }
            });
          }
          setIsLoading(false);
        });
        break;
      }
      case ReadingsEnum.glucose: {
        const schedule = readingsSchedules?.find(s => s?.vitals?.includes(VITALS.SUGAR_LEVEL) && s.enrollment_id === enrollmentId);
        if (schedule) {
          setReadingSchedule(schedule);
        }
        getAlertDefinition(patientId, 'glucose').then(resp => {
          if (resp.type === ALERT_DEFINITION_GET_RESULT) {
            const max = resp.response?.conditions?.[0]?.max;
            const min = resp.response?.conditions?.[0]?.min;
            max && setHighGlucoseConcentration(max);
            min && setLowGlucoseConcentration(min);
          }
          setIsLoading(false);
        });
        break;
      }
      case ReadingsEnum.temperature: {
        const schedule = readingsSchedules?.find(s => s?.vitals?.includes(VITALS.TEMPERATURE) && s.enrollment_id === enrollmentId);
        if (schedule) {
          setReadingSchedule(schedule);
        }
        getAlertDefinition(patientId, 'temperature').then(resp => {
          if (resp.type === ALERT_DEFINITION_GET_RESULT) {
            const max = resp.response?.conditions?.[0]?.max;
            const min = resp.response?.conditions?.[0]?.min;
            max && setHighTemperature(convertCelsiusToFahrenheit(max));
            min && setLowTemperature(convertCelsiusToFahrenheit(min));
          }
          setIsLoading(false);
        });
        break;
      }
      case ReadingsEnum.spirometer: {
        const schedule = readingsSchedules?.find(s => s?.vitals?.includes(VITALS.FORCED_EXPIRATORY_VOLUME_1S) && s.enrollment_id === enrollmentId);
        if (schedule) {
          setReadingSchedule(schedule);
        }
        getAlertDefinition(patientId, 'spirometer').then(resp => {
          if (resp.type === ALERT_DEFINITION_GET_RESULT) {
            resp.response.conditions?.forEach(condition => {
              if (condition.field === VITALS.FORCED_EXPIRATORY_VOLUME_1S) {
                condition.max && setHighFev1(condition.max);
                condition.min && setLowFev1(condition.min);
              } else if (condition.field === VITALS.FORCED_VITAL_CAPACITY) {
                condition.max && setHighFvc(condition.max);
                condition.min && setLowFvc(condition.min);
              }
            });
          }
          setIsLoading(false);
        });
        break;
      }
      case ReadingsEnum.coagulation: {
        const schedule = readingsSchedules?.find(s => s?.vitals?.includes(VITALS.PROTHROMBIN_TIME) && s.enrollment_id === enrollmentId);
        if (schedule) {
          setReadingSchedule(schedule);
        }
        getAlertDefinition(patientId, 'coagulation').then(resp => {
          if (resp.type === ALERT_DEFINITION_GET_RESULT) {
            resp.response?.conditions?.forEach(condition => {
              if (condition.field === VITALS.PROTHROMBIN_TIME) {
                condition.max && setHighPt(condition.max);
                condition.min && setLowPt(condition.min);
              } else if (condition.field === VITALS.INTERNATIONAL_NORMALIZED_RATIO) {
                condition.max && setHighInr(condition.max);
                condition.min && setLowInr(condition.min);
              }
            });
          }
          setIsLoading(false);
        });
        break;
      }
      case ReadingsEnum.pulseox: {
        const schedule = readingsSchedules?.find(s => s?.vitals?.includes(VITALS.OXYGEN_SATURATION) && s.enrollment_id === enrollmentId);
        if (schedule) {
          setReadingSchedule(schedule);
        }
        getAlertDefinition(patientId, 'saturation').then(resp => {
          if (resp.type === ALERT_DEFINITION_GET_RESULT) {
            const min = resp.response?.conditions?.[0]?.min;
            min && setLowSaturation(min);
          }
          setIsLoading(false);
        });
        break;
      }
      case ReadingsEnum.heartRate: {
        const schedule = readingsSchedules?.find(s => s?.vitals?.includes(VITALS.HEART_RATE) && s.enrollment_id === enrollmentId);
        if (schedule) {
          setReadingSchedule(schedule);
        }
        getAlertDefinition(patientId, 'heart_rate').then(resp => {
          if (resp.type === ALERT_DEFINITION_GET_RESULT) {
            const max = resp.response?.conditions?.[0]?.max;
            const min = resp.response?.conditions?.[0]?.min;
            max && setHighHR(max);
            min && setLowHR(min);
          }
          setIsLoading(false);
        });
        break;
      }
      case ReadingsEnum.pm25: {
        const schedule = readingsSchedules?.find(s => s?.vitals?.includes(VITALS.PM25) && s.enrollment_id === enrollmentId);
        if (schedule) {
          setReadingSchedule(schedule);
        }
        getAlertDefinition(patientId, 'air_quality').then(resp => {
          if (resp.type === ALERT_DEFINITION_GET_RESULT) {
            const max = resp.response?.conditions?.[0]?.max;
            max && setHighPm25(max);
          }
          setIsLoading(false);
        });
        break;
      }
      case ReadingsEnum.sleep:
        if (readingsSchedules?.find(s => s?.vitals?.includes(VITALS.SLEEP) && s.enrollment_id === enrollmentId)) {
          setReadingSchedule(readingsSchedules?.find(s => s?.vitals?.includes(VITALS.SLEEP) && s.enrollment_id === enrollmentId));
        }
        setIsLoading(false);
        break;
      case ReadingsEnum.steps:
        if (readingsSchedules?.find(s => s?.vitals?.includes(VITALS.STEPS) && s.enrollment_id === enrollmentId)) {
          setReadingSchedule(readingsSchedules?.find(s => s?.vitals?.includes(VITALS.STEPS) && s.enrollment_id === enrollmentId));
        }
        setIsLoading(false);
        break;
      case ReadingsEnum.activity:
        if (readingsSchedules?.find(s => s?.vitals?.includes(VITALS.EXERCISE_DURATION) && s.enrollment_id === enrollmentId)) {
          setReadingSchedule(readingsSchedules?.find(s => s?.vitals?.includes(VITALS.EXERCISE_DURATION) && s.enrollment_id === enrollmentId));
        }
        setIsLoading(false);
        break;

      default:
        break;
    }
  };

  const getAlert = () => {
    setAlertId(null);
    getAlerts(patientId).then(resp => {
      if (resp.type === ALERTS_GET_RESULT) {
        if (props.selectedMed && props.medInfo) {
          setAlertId(
            resp.response.schedule_missed_doses_alerts?.find(a => a.schedule_id === props.medInfo.id)?.alert_id,
          );
        }
        switch (selectedChart) {
          case ReadingsEnum.weight:
            setAlertId(resp.response.weight_alert_id);
            break;
          case ReadingsEnum.bloodPressure:
            setAlertId(resp.response.blood_pressure_alert_id);
            break;
          case ReadingsEnum.glucose:
            setAlertId(resp.response.glucose_alert_id);
            break;
          case ReadingsEnum.spirometer:
            setAlertId(resp.response.spirometer_alert_id);
            break;
          case ReadingsEnum.temperature:
            setAlertId(resp.response.temperature_alert_id);
            break;
          case ReadingsEnum.pulseox:
            setAlertId(resp.response.saturation_alert_id);
            break;
          case ReadingsEnum.heartRate:
            setAlertId(resp.response.heart_rate_alert_id);
            break;
          case ReadingsEnum.pm25:
            setAlertId(resp.response.air_quality_alert_id);
            break;
          case ReadingsEnum.coagulation:
            setAlertId(resp.response.coagulation_alert_id);
          default:
            break;
        }
      }
    });
  };

  const getData = () => {
    getLimits();
    getAlert();
    if (ReadingsEnumToGoalVitalMap[selectedChart]) {
      getGoals(patientId, ReadingsEnumToGoalVitalMap[selectedChart]).then(resp => {
        if (resp.type === GOALS_GET_RESULT) {
          setGoals(resp.response?.goals?.length ? resp.response : defaultGoals[ReadingsEnumToGoalVitalMap[selectedChart]]);
        }
      });
    }
  }

  useEffect(() => {
    getData();
  }, [selectedChart, patientId, props.selectedMed, enrollmentId]);

  const clearAlert = () => {
    deleteAlert(alertId).then(resp => {
      if (resp.type === DELETE_ALERT_RESULT) {
        onAlertCleared();
        setAlertId(null);
      } else {
        props.showNotification(Strings.clearAlertError, 5000, true);
      }
    });
  };

  const editMissingReadingsSchedule = () => {
    editReadingsSchedule({
      patientId,
      enrollmentId,
      readingSchedule: {[getVitalsForReadingsEnum(selectedChart)?.value]: readingSchedule},
      onSuccess: getLimits,
    });
  }

  const renderAlertThresholdsAndGoals = () => {
    if (props.selectedMed && props.medInfo && props.medInfo.scheduleType !== 'as_needed') {
      return (
        <>
          {props.medInfo.sms_reminder?.missed_doses?.offset && (
            <Input
              name="missedDoses"
              id="missedDoses"
              label={Strings.missedDoses}
              type="number"
              value={props.medInfo.sms_reminder?.missed_doses?.offset}
              disabled
            />
          )}
          {canEdit && !alertId && (
            <Button
              class={HEADER_BUTTON_DARK_BLUE}
              onClick={() => props.editReminders(patientId, props.medInfo)}
              title={props.medInfo.sms_reminder?.missed_doses?.offset ? Strings.change : Strings.setReminder}
            />
          )}
        </>
      );
    }

  const editVitalButton = (vital) => {
    if (!canEdit) return null;
    return (
      <div
        onClick={alertId ? undefined : () => props.editVitalsLimits({
          patientId,
          vital,
          onSuccess: () => {
            getData();
            if (props.onLimitsUpdated) {
              props.onLimitsUpdated();
            }
          },
        })}
        className={`edit-button ${alertId ? 'grey' : ''}`}
        data-tooltip-content={Strings.clearAlertsToSetVitalsLimitsAndGoals}
        data-tooltip-id={alertId ? "tooltip" : undefined}
        data-testid="edit-vitals-alert-button"
      >
        <div className="icon" />
      </div>
    );
  }
  const threshold = readingSchedule?.missed_measurement_reminder?.threshold;
  const scheduleInput = (
    patientStatus === RPM_PATIENT_STATUS.enrolled || patientStatus === RPM_PATIENT_STATUS.activated ? (
      <div className="limit-values" style={{ position: 'relative' }}>
        <div className="limit-label">{Strings.capPatient.missedReadingReminder}:</div>
        <div className="limit-label">
          {
            threshold
            ? threshold === 1
            ? Strings.everyDay
            : Strings.formatString(Strings.everyXDays, threshold)
            : Strings.notSet
          }
        </div>
        <div
          onClick={alertId ? undefined : editMissingReadingsSchedule}
          className={`edit-button ${alertId ? 'grey' : ''}`}
          data-testid="edit-missed-reading-reminder-button"
          data-tooltip-content={Strings.clearAlertsToSetMissedReadingsReminder}
          data-tooltip-id={alertId || isLoading ? "tooltip" : undefined}
        >
          <div className="icon" />
        </div>
      </div>
    ) : null
  );

  const goalsSet = goals?.goals?.find(goal =>
    ReadingsEnumToGoalFieldsMap[selectedChart]?.includes(goal.field)
    && (goal.max != null || goal.min != null)
  );
  switch (selectedChart) {
      case ReadingsEnum.weight: {
        const weightGoal = goals?.goals?.find(goal => goal.field === 'weight');
        const weightFrom = weightGoal?.min ? roundToDecimal(convertKilogramToPound(weightGoal.min)) : null;
        const weightTo = weightGoal?.max ? roundToDecimal(convertKilogramToPound(weightGoal.max)) : null;
        return (
          <>
            {scheduleInput}
            <div className="section-header-inputs">
              {showAlertThresholds && (
                <div className="limit-values">
                  <div className="limit-label">{`${Strings.low} / ${Strings.high} ${Strings.weight}:`}</div>
                  <div className="limit-label">{`${lowWeight || '-'} / ${highWeight || '-'}`}</div>
                </div>
              )}
              {showGoals && (
                <div className="limit-values">
                  <div className="limit-label">{`${Strings.weight} ${Strings.goal}:`}</div>
                  <div className="limit-label">
                    {!goalsSet ? '-' : `${weightFrom || '-'} - ${weightTo || '-'}`}
                  </div>
                </div>
              )}
              {showAlertThresholds || showGoals ? editVitalButton('weight') : null}
            </div>
          </>
        );
      }

      case ReadingsEnum.bloodPressure: {
        return (
          <>
            {scheduleInput}
            <div className="section-header-inputs">
              {showAlertThresholds && (
                <>
                  <div className="limit-values">
                    <div className="limit-label">{`${Strings.low} / ${Strings.high} Systolic:`}</div>
                    <div className="limit-label">{`${lowSystolic || '-'} / ${highSystolic || '-'}`}</div>
                  </div>
                  <div className="limit-values">
                    <div className="limit-label">{`${Strings.low} / ${Strings.high} Diastolic:`}</div>
                    <div className="limit-label">{`${lowDiastolic || '-'} / ${highDiastolic || '-'}`}</div>
                  </div>
                </>
              )}
              {showGoals && (
                <>
                  <div className="limit-values">
                    <div className="limit-label">{`Systolic ${Strings.goal}:`}</div>
                    <div className="limit-label">{!goalsSet ? '-' : `${goals?.goals?.find(goal => goal.field === 'pressure_systolic')?.min || '-'} - ${goals?.goals?.find(goal => goal.field === 'pressure_systolic')?.max || '-'}`}</div>
                  </div>
                  <div className="limit-values">
                    <div className="limit-label">{`Diastolic ${Strings.goal}:`}</div>
                    <div className="limit-label">{!goalsSet ? '-' : `${goals?.goals?.find(goal => goal.field === 'pressure_diastolic')?.min || '-'} - ${goals?.goals?.find(goal => goal.field === 'pressure_diastolic')?.max || '-'}`}</div>
                  </div>
                </>
              )}
              {showAlertThresholds || showGoals ? editVitalButton('blood_pressure') : null}
            </div>
          </>
        );
      }

      case ReadingsEnum.glucose: {
        return (
          <>
            {scheduleInput}
            <div className="section-header-inputs">
              {showAlertThresholds && (
                <div className="limit-values">
                  <div className="limit-label">{`${Strings.low} / ${Strings.high} Glucose:`}</div>
                  <div className="limit-label">
                    {`${lowGlucoseConcentration != null ? roundToDecimal(lowGlucoseConcentration, 2) : '-'} / ${highGlucoseConcentration != null ? roundToDecimal(highGlucoseConcentration, 2) : '-'}`}
                  </div>
                </div>
              )}
              {showGoals && (
                <div className="limit-values">
                  <div className="limit-label">{`Glucose ${Strings.goal}:`}</div>
                  <div className="limit-label">{!goalsSet ? '-' : `${goals?.goals?.find(goal => goal.field === 'sugar_level')?.min || '-'} - ${goals?.goals?.find(goal => goal.field === 'sugar_level')?.max || '-'}`}</div>
                  {showAlertThresholds || showGoals ? editVitalButton('glucose') : null}
                </div>
              )}
            </div>
          </>
        );
      }

      case ReadingsEnum.temperature: {
        const temperatureGoal = goals?.goals?.find(goal => goal.field === 'temperature');
        const temperatureFrom = temperatureGoal?.min == null ? null : roundToDecimal(convertCelsiusToFahrenheit(temperatureGoal.min), 2);
        const temperatureTo = temperatureGoal?.max == null ? null : roundToDecimal(convertCelsiusToFahrenheit(temperatureGoal.max), 2);
        return (
          <>
            {scheduleInput}
            <div className="section-header-inputs">
              {showAlertThresholds && (
                <div className="limit-values">
                  <div className="limit-label">{`${Strings.low} / ${Strings.high} ${Strings.temperature}:`}</div>
                  <div className="limit-label">
                    {`${lowTemperature != null ? roundToDecimal(lowTemperature, 2) : '-'} / ${highTemperature != null ? roundToDecimal(highTemperature, 2) : '-'}`}
                  </div>
                </div>
              )}
              {showGoals && (
                <div className="limit-values">
                  <div className="limit-label">{`Temperature ${Strings.goal}:`}</div>
                  <div className="limit-label">
                    {!goalsSet ? '-' : `${temperatureFrom || '-'} - ${temperatureTo || '-'}`}
                  </div>
                </div>
              )}
              {showAlertThresholds || showGoals ? editVitalButton('temperature') : null}
            </div>
          </>
        );
      }

      case ReadingsEnum.spirometer: {
        return (
          <>
            {scheduleInput}
            <div className="section-header-inputs">
              {showAlertThresholds && (
                <>
                  <div className="limit-values">
                    <div className="limit-label">{`${Strings.low} / ${Strings.high} Fev1:`}</div>
                    <div className="limit-label">
                      {`${lowFev1 != null ? roundToDecimal(lowFev1, 2) : '-'} / ${highFev1 != null ? roundToDecimal(highFev1, 2) : '-'}`}
                    </div>
                  </div>
                  <div className="limit-values">
                    <div className="limit-label">{`${Strings.low} / ${Strings.high} Fvc:`}</div>
                    <div className="limit-label">
                      {`${lowFvc != null ? roundToDecimal(lowFvc, 2) : '-'} / ${highFvc != null ? roundToDecimal(highFvc, 2) : '-'}`}
                    </div>
                  </div>
                </>
              )}
              {showAlertThresholds ? editVitalButton('spirometer') : null}
            </div>
          </>
        );
      }

      case ReadingsEnum.pulseox: {
        return (
          <>
            {scheduleInput}
            <div className="section-header-inputs">
              {showAlertThresholds && (
                <div className="limit-values">
                  <div className="limit-label">{`${Strings.low} ${Strings.saturation}:`}</div>
                  <div className="limit-label">{lowSaturation || '-'}</div>
                </div>
              )}
              {showGoals && (
                <div className="limit-values">
                  <div className="limit-label">{`${Strings.saturation} ${Strings.goal}:`}</div>
                  <div className="limit-label">
                    {!goalsSet ? '-' : `${goals?.goals?.find(goal => goal.field === 'oxygen_saturation')?.min || '-'} - ${goals?.goals?.find(goal => goal.field === 'oxygen_saturation')?.max || '-'}`}
                  </div>
                </div>
              )}
              {editVitalButton('saturation')}
            </div>
          </>
        );
      }

      case ReadingsEnum.heartRate: {
        return (
          <>
            {scheduleInput}
            <div className="section-header-inputs">
              {showAlertThresholds && (
                <div className="limit-values">
                  <div className="limit-label">{`${Strings.low} / ${Strings.high} ${Strings.heartRate}:`}</div>
                  <div className="limit-label">{`${lowHR || '-'} / ${highHR || '-'}`}</div>
                </div>
              )}
              {showGoals && (
                <div className="limit-values">
                  <div className="limit-label">{`${Strings.heartRate} ${Strings.goal}:`}</div>
                  <div className="limit-label">
                    {!goalsSet ? '-' : `${goals?.goals?.find(goal => goal.field === 'heart_rate')?.min || '-'} - ${goals?.goals?.find(goal => goal.field === 'heart_rate')?.max || '-'}`}
                  </div>
                </div>
              )}
              {editVitalButton('heart_rate')}
            </div>
          </>
        );
      }

      case ReadingsEnum.pm25: {
        return (
          <>
            {scheduleInput}
            <div className="section-header-inputs">
              {showAlertThresholds && (
                <>
                  <div className="limit-values">
                    <div className="limit-label">{`${Strings.high} ${Strings.pm25}:`}</div>
                    <div className="limit-label">{highPm25 || '-'}</div>
                  </div>
                  {editVitalButton('air_quality')}
                </>
              )}
            </div>
          </>
        );
      }

      case ReadingsEnum.coagulation: {
        return (
          <>
            {scheduleInput}
            <div className="section-header-inputs">
              {showAlertThresholds && (
                <>
                  <div className="limit-values">
                    <div className="limit-label">{`${Strings.low} / ${Strings.high} PT:`}</div>
                    <div className="limit-label">{`${lowPt || '-'} / ${highPt || '-'}`}</div>
                  </div>
                  <div className="limit-values">
                    <div className="limit-label">{`${Strings.low} / ${Strings.high} INR:`}</div>
                    <div className="limit-label">{`${lowInr || '-'} / ${highInr || '-'}`}</div>
                  </div>
                  {editVitalButton('coagulation')}
                </>
              )}
            </div>
          </>
        );
      }

      default: {
        return null;
      }
    }
  };

  // Vitals without alert support (e.g. Steps, Sleep, Activity) have no alert configuration to
  // show. Render nothing so no empty/misleading section is displayed. The "nothing selected"
  // placeholder and medication alerts are still handled below.
  if (selectedChart && !props.selectedMed && !ReadingsEnumToAlertVitalMap[selectedChart]) {
    return null;
  }

  return (
    <div className="section-header">
      {props.selectedMed ? (
        <div className="alert-title">{`${props.medInfo?.medicationName} ${
          props.medInfo?.scheduleType !== 'as_needed' ? Strings.alerts : ''
        }`}</div>
      ) : (
        <div>{selectedChart ? Strings.alertsEnum[selectedChart] : Strings.capPatient.noAlertsToShow}</div>
      )}
      <div className="section-header-right">
        {renderAlertThresholdsAndGoals()}
        {alertId && canEdit && (
          <Button class={HEADER_BUTTON_DARK_BLUE} onClick={clearAlert} title={Strings.clearAlert} />
        )}
      </div>
    </div>
  );
}

LimitsSection.propTypes = {
  selectedChart: PropTypes.string.isRequired,
  deleteAlert: PropTypes.func.isRequired,
  setAlertDefinition: PropTypes.func.isRequired,
  getAlertDefinition: PropTypes.func.isRequired,
  getAlerts: PropTypes.func.isRequired,
  onAlertCleared: PropTypes.func.isRequired,
  onLimitsUpdated: PropTypes.func,
  showNotification: PropTypes.func.isRequired,
  patientId: PropTypes.string.isRequired,
  getReadingsSchedules: PropTypes.func.isRequired,
  editReadingsSchedule: PropTypes.func.isRequired,
};

const mapDispatchToProps = (dispatch, props) => ({
  setAlertDefinition: (patientId, alertDefinition) => dispatch(actions.setAlertDefinition(patientId, alertDefinition)),
  getAlertDefinition: (patientId, type) => dispatch(actions.getAlertDefinition(patientId, type)),
  getGoals: (patientId, type) => dispatch(actions.getGoals(patientId, type)),
  showNotification: (message, timeout, error) => dispatch(notificationActions.show(message, timeout, error)),
  getAlerts: patientId => dispatch(actions.getAlerts(patientId)),
  deleteAlert: id => dispatch(alertActions.deleteAlert(id)),
  getReadingsSchedules: patientId => dispatch(patientActions.getReadingsSchedules(patientId)),
  editReminders: (patientId, schedule) =>
    dispatch(
      openModalAction('edit-rpm-schedule', {
        patient: { id: patientId },
        schedule,
        actionType: EDIT_SCHEDULE,
        onSuccess: props.onAlertCleared,
        pages: [EditRpmScheduleModalPages.reminders],
        ignoreFilters: true,
      }),
    ),
  editReadingsSchedule: data => dispatch(openModalAction('edit-reading-schedule', data)),
  editVitalsLimits: data => dispatch(openModalAction(EDIT_VITALS_LIMITS_AND_GOALS_MODAL_NAME, data)),
});

export default connect(null, mapDispatchToProps)(LimitsSection);
